import type { ConditionAndServiceChange } from "@slfinrtl/ui-bff-routes"
import {
	accidentFreeMessages,
	knownAccidentsMessages,
	maintenanceScopeOptionsMessages,
	previousOwnerUsageHistoryOptionsMessages,
} from "@slfinrtl/ui-bff-routes"
import type { IntlShape } from "react-intl"
import { useIntl } from "react-intl"
import { emptyPlaceholder } from "src/common/emptyPlaceholder"
import { commonMessages } from "src/common/messages/common.messages"
import { conditionAndServiceFieldMessages } from "src/components"
import type { InventoryInformationVehicleConditionTypeNewChangeFields } from "src/components"
import { useConfig } from "src/hooks"

/* eslint-disable perfectionist/sort-objects */
// order is relevant to be in sync with form and list
export const useConditionAndServiceChanges = (
	change: ConditionAndServiceChange | undefined,
): { label: string; value: string | number }[] => {
	const { formatMessage } = useIntl()
	const { accidentFreeLabel } = useConfig()

	if (change) {
		const mappedRecord: Record<
			keyof InventoryInformationVehicleConditionTypeNewChangeFields,
			{ label: string; value: string | number } | undefined
		> = {
			// General information
			firstRegistrationDate:
				change.firstRegistrationDate !== undefined
					? {
							label: formatMessage(conditionAndServiceFieldMessages.firstRegistrationDateLabel),
							value: change.firstRegistrationDate ?? emptyPlaceholder,
						}
					: undefined,
			previousOwners:
				change.previousOwners !== undefined
					? {
							label: formatMessage(conditionAndServiceFieldMessages.previousOwnersLabel),
							value: change.previousOwners ?? emptyPlaceholder,
						}
					: undefined,
			previousOwnerUsageHistory:
				change.previousOwnerUsageHistory !== undefined
					? {
							label: formatMessage(conditionAndServiceFieldMessages.previousOwnerUsageHistoryLabel),
							value: change.previousOwnerUsageHistory
								? formatMessage(
										previousOwnerUsageHistoryOptionsMessages[change.previousOwnerUsageHistory],
									)
								: emptyPlaceholder,
						}
					: undefined,
			batteryCapacityPercentage:
				change.batteryCapacityPercentage !== undefined
					? {
							label: formatMessage(conditionAndServiceFieldMessages.batteryCapacityLabel),
							value: change.batteryCapacityPercentage ?? emptyPlaceholder,
						}
					: undefined,

			// Damage History
			accidentFree:
				change.accidentFree !== undefined
					? {
							label: formatMessage(
								accidentFreeLabel
									? conditionAndServiceFieldMessages.accidentFreeLabel
									: conditionAndServiceFieldMessages.knownAccidentsLabel,
							),
							value: change.accidentFree
								? formatMessage(
										accidentFreeLabel
											? accidentFreeMessages[change.accidentFree]
											: knownAccidentsMessages[change.accidentFree],
									)
								: emptyPlaceholder,
						}
					: undefined,
			accidentDescription:
				change.accidentDescription !== undefined
					? {
							label: formatMessage(conditionAndServiceFieldMessages.accidentDescriptionLabel),
							value: change.accidentDescription
								? Object.entries(change.accidentDescription)
										.map(([key, value]) => `${key}: ${value}`)
										.join(", ")
								: emptyPlaceholder,
						}
					: undefined,

			// Service History
			fullServiceHistory:
				change.fullServiceHistory !== undefined
					? {
							label: formatMessage(conditionAndServiceFieldMessages.fullServiceHistoryLabel),
							value: getYesOrNo(formatMessage, change.fullServiceHistory),
						}
					: undefined,
			fullServiceHistoryInPorscheCenter:
				change.fullServiceHistoryInPorscheCenter !== undefined
					? {
							label: formatMessage(
								conditionAndServiceFieldMessages.fullServiceHistoryInPorscheCenterLabel,
							),
							value: getYesOrNo(formatMessage, change.fullServiceHistoryInPorscheCenter),
						}
					: undefined,

			// Latest maintenance
			latestMaintenanceDate:
				change.latestMaintenanceDate !== undefined
					? {
							label: formatMessage(conditionAndServiceFieldMessages.latestMaintenanceDateTitle),
							value: change.latestMaintenanceDate ?? emptyPlaceholder,
						}
					: undefined,
			latestMaintenanceScope:
				change.latestMaintenanceScope !== undefined
					? {
							label: formatMessage(conditionAndServiceFieldMessages.latestMaintenanceDateTitle),
							value: change.latestMaintenanceScope
								? formatMessage(maintenanceScopeOptionsMessages[change.latestMaintenanceScope])
								: emptyPlaceholder,
						}
					: undefined,
			latestMaintenanceMileage:
				change.latestMaintenanceMileage !== undefined
					? {
							label: formatMessage(conditionAndServiceFieldMessages.latestMaintenanceMileageLabel),
							value: change.latestMaintenanceMileage ?? emptyPlaceholder,
						}
					: undefined,
			latestMaintenanceWorkshop:
				change.latestMaintenanceWorkshop !== undefined
					? {
							label: formatMessage(conditionAndServiceFieldMessages.latestMaintenanceWorkshopLabel),
							value: change.latestMaintenanceWorkshop ?? emptyPlaceholder,
						}
					: undefined,

			// Pre-contractual Information
			nextGeneralInspectionDate:
				change.nextGeneralInspectionDate !== undefined
					? {
							label: formatMessage(conditionAndServiceFieldMessages.nextGeneralInspectionDateLabel),
							value: change.nextGeneralInspectionDate ?? emptyPlaceholder,
						}
					: undefined,
			renewGeneralInspectionOnSale:
				change.renewGeneralInspectionOnSale !== undefined
					? {
							label: formatMessage(conditionAndServiceFieldMessages.newOnSaleLabel),
							value: getYesOrNo(formatMessage, change.renewGeneralInspectionOnSale),
						}
					: undefined,
			nonSmoker:
				change.nonSmoker !== undefined
					? {
							label: formatMessage(conditionAndServiceFieldMessages.nonSmokerLabel),
							value: getYesOrNo(formatMessage, change.nonSmoker),
						}
					: undefined,
			formerRentalCar:
				change.formerRentalCar !== undefined
					? {
							label: formatMessage(conditionAndServiceFieldMessages.formerRentalCarLabel),
							value: getYesOrNo(formatMessage, change.formerRentalCar),
						}
					: undefined,
			previousDamage:
				change.previousDamage !== undefined
					? {
							label: formatMessage(conditionAndServiceFieldMessages.previousDamageLabel),
							value: getYesOrNo(formatMessage, change.previousDamage),
						}
					: undefined,
			previousDamageAmount:
				change.previousDamageAmount !== undefined
					? {
							label: formatMessage(conditionAndServiceFieldMessages.previousDamageAmountLabel),
							value: change.previousDamageAmount ?? emptyPlaceholder,
						}
					: undefined,
			previousDamageDescription:
				change.previousDamageDescription !== undefined
					? {
							label: formatMessage(conditionAndServiceFieldMessages.previousDamageDescriptionLabel),
							value: change.previousDamageDescription ?? emptyPlaceholder,
						}
					: undefined,
		}

		return Object.values(mappedRecord).filter((item) => item !== undefined)
	}
	return []
}

const getYesOrNo = (formatMessage: IntlShape["formatMessage"], value: boolean | null): string =>
	formatMessage(value ? commonMessages.yes : commonMessages.no)
